import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { permissionGroupApi, workerApi } from '../../api';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import LoadingSpinner from '../../components/common/LoadingSpinner';

// audit_log_view is deliberately excluded — it's not a selectable permission
// for any regular group, only ever toggled on the Full Access system group,
// only by Super Admin, via its own dedicated control below (see
// FullAccessAuditLogToggle). No other group can view or configure it.
const ALL_PERMISSIONS = [
  { key: 'dashboard_view',          label: 'Dashboard Access',         desc: 'View analytics KPIs, charts and financial trends',       group: 'Dashboard' },
  { key: 'site_group_management',   label: 'Site & Group Management',  desc: 'Manage groups, sites and their details',                  group: 'Master Data' },
  // Materials is a deliberately isolated item pillar, mirroring IT's own
  // View/Management/Issue/Add Stock/Edit Stock/Info shape exactly. Unlike
  // IT, Materials has no direct "repair and return to stock" pipeline —
  // Damage Flagging/Approval (flag → approve → archive to Discard History)
  // is its equivalent instead, so those two live here rather than a
  // repair_management-style entry. Functionally unchanged from before —
  // only the label/grouping moved, not what each permission gates.
  { key: 'inventory_view',          label: 'Material View',            desc: 'View material inventory across sites — required to see the Materials section at all', group: 'Materials' },
  { key: 'master_data_management',  label: 'Material Management',      desc: 'Create and edit material (safety kit) types',             group: 'Materials' },
  { key: 'worker_kit_issue',        label: 'Material Issue',           desc: 'Issue and return safety kit materials to/from workers — independent of Material Management, so a group can issue materials without being able to create/edit worker records', group: 'Materials' },
  { key: 'inventory_add',           label: 'Material Add Stock',       desc: 'Add new stock entries and restock existing site/kit rows', group: 'Materials' },
  { key: 'inventory_edit',          label: 'Material Edit Stock',      desc: 'Modify or delete existing stock entries and correct quantities', group: 'Materials' },
  { key: 'materials_info_manage',   label: 'Material Information — Materials',  desc: 'Add, edit, and delete custom key-value fields for materials, and their values per individual unit', group: 'Materials' },
  { key: 'damage_flagging',         label: 'Damage Flagging',          desc: 'Mark an issued material Damaged, upload photos, and submit for approval', group: 'Materials' },
  { key: 'damage_approval',         label: 'Damage Approval',          desc: 'Approve or reject items in the Pending Damage Approval queue — separate from Damage Flagging by design', group: 'Materials' },
  // Stationery mirrors the same shape — Stationery View was previously
  // missing from this list entirely (Stationery reads had no gating
  // permission at all until it was added), which is exactly why it wasn't
  // visible here before now.
  { key: 'stationery_view',         label: 'Stationery View',          desc: 'View stationery inventory and issuances across sites — required to see the Stationery section at all', group: 'Stationery' },
  { key: 'stationery_manage',       label: 'Stationery Management',    desc: 'Create, edit, and delete stationery item types', group: 'Stationery' },
  { key: 'stationery_issue',        label: 'Stationery Issue',         desc: 'Issue and return stationery items to/from workers — independent of Stationery Management, so a group can issue stationery without being able to manage the item catalog', group: 'Stationery' },
  { key: 'stationery_add',          label: 'Stationery Add Stock',     desc: 'Add new stationery stock entries and restock existing site/item rows', group: 'Stationery' },
  { key: 'stationery_edit',         label: 'Stationery Edit Stock',    desc: 'Modify existing stationery stock entries and correct quantities', group: 'Stationery' },
  { key: 'stationery_info_manage',  label: 'Material Information — Stationery', desc: 'Add, edit, and delete custom key-value fields for stationery, and their values per individual unit', group: 'Stationery' },
  { key: 'repair_management',       label: 'Stationery Repair & Maintenance', desc: 'Mark a Stationery Durable as Repaired, send/return it for maintenance, or mark it Beyond Recovery (scrapped)', group: 'Stationery' },
  // IT is a third item pillar, isolated on its own — unlike Materials/
  // Stationery, it_view is required just to VIEW IT items/inventory/
  // issuances at all (see it.js's route gates); no other permission here
  // grants any IT access.
  { key: 'it_view',                 label: 'IT View',                  desc: 'View IT items, inventory, and issuances — required to see the IT section at all; no other permission (including Material View) grants this', group: 'IT' },
  { key: 'it_manage',               label: 'IT Management',            desc: 'Create, edit, and delete IT item types', group: 'IT' },
  { key: 'it_issue',                label: 'IT Issue',                 desc: 'Issue and return IT items to/from workers — independent of IT Management, so a group can issue IT items without being able to manage the item catalog', group: 'IT' },
  { key: 'it_add',                  label: 'IT Add Stock',             desc: 'Add new IT stock entries and restock existing site/item rows', group: 'IT' },
  { key: 'it_edit',                 label: 'IT Edit Stock',            desc: 'Modify existing IT stock entries and correct quantities', group: 'IT' },
  { key: 'it_info_manage',          label: 'Material Information — IT', desc: 'Add, edit, and delete custom key-value fields for IT items, and their values per individual unit', group: 'IT' },
  { key: 'it_repair_management',    label: 'IT Repair & Maintenance',  desc: 'Mark an IT Durable as Repaired, send/return it for maintenance, or mark it Beyond Recovery (scrapped)', group: 'IT' },
  { key: 'requirement_creation',    label: 'Requirement Creation',     desc: 'Raise new material requirements',                        group: 'Transfers & Requirements' },
  { key: 'transfer_request',        label: 'Transfer',                 desc: 'Create inter-site material transfers. If the creator also holds Transfer Approval it goes straight to dispatch, otherwise it waits for an approver first', group: 'Transfers & Requirements' },
  { key: 'transfer_creation_approval', label: 'Transfer Approval',     desc: 'Approve transfers created by someone who holds Transfer but not this permission — independent of Transfer, so it can gate approval separately (e.g. if Storekeepers are ever granted Transfer directly)', group: 'Transfers & Requirements' },
  { key: 'transfer_approval',       label: 'Procurement Dispatch',     desc: 'Mark externally-procured material as dispatched, or cancel it, for open requirements', group: 'Transfers & Requirements' },
  { key: 'cross_group_transfer',    label: 'Cross-Group Transfers',    desc: 'Transfer materials, stationery, or IT items between sites in different site groups', group: 'Transfers & Requirements' },
  { key: 'request_management',      label: 'Request Management',       desc: 'View the Requests panel, issue directly from a request, and receive new-request notifications', group: 'Transfers & Requirements' },
  { key: 'worker_management',       label: 'Worker Management',        desc: 'Create, edit, and delete worker records',                 group: 'Workers' },
  { key: 'worker_site_assignment',  label: 'Worker Site Assignment',   desc: 'Assign workers to sites and manage transfers',            group: 'Workers' },
  { key: 'site_correction_review',  label: 'Site Correction Review',   desc: 'View and act on workers\' "my assigned site is wrong" claims — approve (corrects their site) or dismiss', group: 'Workers' },
  { key: 'salary_deduction_view',   label: 'Salary Deductions Panel',  desc: 'View salary deduction records and cycle summaries',       group: 'Workers' },
  { key: 'guest_management',        label: 'Guest Management',         desc: 'Add and manage Guest users under Inventory → Workers',   group: 'Workers' },
  { key: 'manage_subcontractors',    label: 'Manage Subcontractors',    desc: 'Add, edit, and delete subcontractors',                    group: 'Workers' },
  { key: 'slip_generation',         label: 'Material Slip Generation', desc: 'Generate a printable A4 material slip for any worker',   group: 'Inventory' },
  { key: 'asset_history_view',      label: 'Discard History',          desc: 'View Damaged, Scrapped, and Validity Over items archived out of active tracking', group: 'Inventory' },
  { key: 'item_lookup_view',        label: 'Item Lookup',              desc: 'View the Item Lookup page/panel — pick one Material/Stationery/IT item and see every unit + holder (or aggregate stock/issued) across every site. Independent of Material View.', group: 'Inventory' },
  { key: 'permission_management',   label: 'Permission Management',    desc: 'Manage permission groups and assign them to workers',    group: 'Administration' },
];

const emptyForm = { name: '', description: '', permissions: [], siteAccessLevel: 'ALL', assignableGroupIds: [] };

export default function PermissionGroups() {
  const { user } = useAuth();
  const [groups, setGroups]   = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [auditLogBusy, setAuditLogBusy] = useState(false);

  const [createOpen, setCreateOpen]   = useState(false);
  const [editTarget, setEditTarget]   = useState(null);
  const [viewTarget, setViewTarget]   = useState(null);
  const [membersTarget, setMembersTarget] = useState(null);  // separate members modal
  const [confirm, setConfirm]         = useState(null);
  const [confirmDefault, setConfirmDefault] = useState(null); // { id, name, step: 1 | 2 }

  const [form, setForm]         = useState(emptyForm);
  const [memberIds, setMemberIds] = useState([]);
  const [error, setError] = useState('');
  const [membersError, setMembersError] = useState('');

  const load = async () => {
    try {
      const [g, w] = await Promise.all([permissionGroupApi.list(), workerApi.list({ isActive: true })]);
      setGroups(g.data.data);
      setWorkers(w.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load permission groups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // ── Create ──────────────────────────────────────────────────────────────────
  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await permissionGroupApi.create({ ...form, workerIds: [] });
      setCreateOpen(false);
      setForm(emptyForm);
      load();
    } catch (err) { setError(err.response?.data?.message || 'Error creating group'); }
  };

  // ── Edit (name / description / permissions only) ─────────────────────────
  const openEdit = (g) => {
    setForm({
      name: g.name,
      description: g.description || '',
      permissions: (g.permissions || []).map((p) => p.permission),
      siteAccessLevel: g.siteAccessLevel || 'ALL',
      assignableGroupIds: (g.assignableGroups || []).map((ag) => ag.id),
    });
    setEditTarget(g);
    setError('');
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      // Preserve existing members when saving group details
      const currentWorkerIds = workersInGroup(editTarget.id).map((w) => w.id);
      await permissionGroupApi.update(editTarget.id, { ...form, workerIds: currentWorkerIds });
      setEditTarget(null);
      load();
    } catch (err) { setError(err.response?.data?.message || 'Error updating group'); }
  };

  // ── Edit Members ─────────────────────────────────────────────────────────
  const openMembers = (g) => {
    setMemberIds(workersInGroup(g.id).map((w) => w.id));
    setMembersTarget(g);
    setMembersError('');
  };

  const handleMembers = async (e) => {
    e.preventDefault();
    setMembersError('');
    try {
      const g = membersTarget;
      await permissionGroupApi.update(g.id, {
        name: g.name,
        description: g.description || '',
        permissions: (g.permissions || []).map((p) => p.permission),
        siteAccessLevel: g.siteAccessLevel || 'ALL',
        // Must be included here too — omitting it would reset this group's
        // assignable-groups allowlist to empty on every members-only save,
        // same class of bug as the earlier siteAccessLevel omission.
        assignableGroupIds: (g.assignableGroups || []).map((ag) => ag.id),
        workerIds: memberIds,
      });
      setMembersTarget(null);
      load();
    } catch (err) { setMembersError(err.response?.data?.message || 'Error updating members'); }
  };

  const handleSetDefault = async (id) => {
    try {
      await permissionGroupApi.setDefault(id);
      setConfirmDefault(null);
      load();
    } catch (err) {
      setConfirmDefault(null);
      setError(err.response?.data?.message || 'Failed to set default group');
    }
  };

  // Full Access is hidden from list()'s response entirely for anyone who
  // isn't super_admin — so its mere presence in `groups` already implies the
  // viewer is super_admin, no separate role check needed here.
  const fullAccessGroup = groups.find((g) => g.isSystem);
  const fullAccessHasAuditLog = fullAccessGroup?.permissions?.some((p) => p.permission === 'audit_log_view');

  const handleToggleAuditLog = async () => {
    if (!fullAccessGroup) return;
    setAuditLogBusy(true);
    setError('');
    try {
      await permissionGroupApi.toggleAuditLogView(fullAccessGroup.id, !fullAccessHasAuditLog);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update Audit Log Access');
    } finally {
      setAuditLogBusy(false);
    }
  };

  const togglePerm = (key) =>
    setForm((f) => ({
      ...f,
      permissions: f.permissions.includes(key)
        ? f.permissions.filter((p) => p !== key)
        : [...f.permissions, key],
    }));

  const toggleAssignableGroup = (id) =>
    setForm((f) => ({
      ...f,
      assignableGroupIds: f.assignableGroupIds.includes(id)
        ? f.assignableGroupIds.filter((x) => x !== id)
        : [...f.assignableGroupIds, id],
    }));

  const toggleMemberId = (id) =>
    setMemberIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  // Returns which group (if any) a worker is currently assigned to, excluding the target group
  const workerOtherGroup = (workerId, excludeGroupId) =>
    groups.find(
      (g) => g.id !== excludeGroupId && workersInGroup(g.id).some((w) => w.id === workerId)
    );

  // Excludes workers who only fall into this group via the "no explicit
  // group → default group" fallback (see workers.js's withEffectiveGroup) —
  // membership management here is about explicit assignment only, so an
  // unassigned worker shouldn't be shown as a member of, or locked out of
  // being moved into, whatever group currently happens to be default.
  const workersInGroup = (groupId) =>
    workers.filter((w) => w.permissionGroup?.id === groupId && !w.permissionGroup?.isImplicitDefault);

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <PageHeader
        title="Permission Groups"
        subtitle="Define role-based access control groups and their permissions"
        action={
          <button
            className="btn-primary"
            onClick={() => { setForm(emptyForm); setCreateOpen(true); setError(''); }}
          >
            + Add Group
          </button>
        }
      />

      {error && <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4">{error}</p>}

      {/* Full Access — visible only to Super Admin (the backend hides it from
          everyone else's list() response entirely, not just here). No
          Edit/Members/Delete/Set-Default here — the only thing manageable is
          the optional Audit Log Access permission, and only by Super Admin. */}
      {fullAccessGroup && (
        <div className="card mb-4 border-slate-300 ring-1 ring-slate-200">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-gray-900">{fullAccessGroup.name}</h3>
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-slate-100 text-slate-600 border border-slate-300 px-2 py-0.5 rounded-full">
                🔒 System — Super Admin only
              </span>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <span className="text-sm text-gray-700">Audit Log Access</span>
              <input
                type="checkbox"
                className="rounded"
                checked={!!fullAccessHasAuditLog}
                disabled={auditLogBusy}
                onChange={handleToggleAuditLog}
              />
            </label>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            All permissions, always. Audit Log Access is the one optional permission within Full Access — no other permission group can view or configure it.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {/* System groups (Full Access) can't be edited here, so they're
            excluded from this management list entirely. */}
        {groups.filter((g) => !g.isSystem).map((g) => {
          const members = workersInGroup(g.id);
          return (
            <div key={g.id} className={`card flex flex-col gap-3 ${g.isDefault ? 'border-amber-300 ring-1 ring-amber-200' : ''} ${g.isSystem ? 'border-slate-300 ring-1 ring-slate-200' : ''}`}>
              {/* Header */}
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-gray-900">{g.name}</h3>
                    {g.isDefault && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-amber-100 text-amber-700 border border-amber-300 px-2 py-0.5 rounded-full">
                        ★ Default
                      </span>
                    )}
                    {g.isSystem && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-slate-100 text-slate-600 border border-slate-300 px-2 py-0.5 rounded-full">
                        🔒 System
                      </span>
                    )}
                    <SiteAccessBadge level={g.siteAccessLevel} />
                  </div>
                  {g.description && (
                    <p className="text-xs text-gray-500 mt-0.5">{g.description}</p>
                  )}
                </div>
                <ActionMenu>
                  <ActionItem onClick={() => setViewTarget(g)}>View</ActionItem>
                  {!g.isSystem && <ActionItem onClick={() => openEdit(g)}>Edit</ActionItem>}
                  <ActionItem onClick={() => openMembers(g)}>Members</ActionItem>
                  {!g.isDefault && !g.isSystem && (
                    <ActionItem onClick={() => setConfirmDefault({ id: g.id, name: g.name, step: 1 })}>☆ Set Default</ActionItem>
                  )}
                  {!g.isSystem && (
                    <ActionItem danger onClick={() => setConfirm({ id: g.id, name: g.name, memberCount: members.length })}>Delete</ActionItem>
                  )}
                </ActionMenu>
              </div>

              {/* Permissions */}
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                  Permissions ({g.permissions?.length || 0})
                </p>
                <div className="flex flex-wrap gap-1">
                  {g.permissions?.length > 0
                    ? g.permissions.map((pe) => {
                        const meta = ALL_PERMISSIONS.find((p) => p.key === pe.permission);
                        return (
                          <span key={pe.permission} className="bg-blue-50 text-blue-700 text-xs px-2 py-0.5 rounded-full">
                            {meta?.label || pe.permission}
                          </span>
                        );
                      })
                    : <span className="text-gray-400 text-xs">No permissions assigned</span>
                  }
                </div>
              </div>

              {/* Members */}
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                  Members ({members.length})
                </p>
                {members.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {members.slice(0, 5).map((w) => (
                      <span key={w.id} className="bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded-full">
                        {w.name}
                      </span>
                    ))}
                    {members.length > 5 && (
                      <span className="text-gray-400 text-xs px-1">+{members.length - 5} more</span>
                    )}
                  </div>
                ) : (
                  <span className="text-gray-400 text-xs">No members</span>
                )}
              </div>
            </div>
          );
        })}

        {groups.length === 0 && (
          <div className="col-span-3 text-center py-16 text-gray-400">
            <p className="text-4xl mb-3">🔐</p>
            <p className="font-medium">No permission groups yet</p>
            <p className="text-sm mt-1">Create groups to control what workers can access</p>
          </div>
        )}
      </div>

      {/* ── Create Modal ─────────────────────────────────────────────────── */}
      {createOpen && (
        <Modal title="Create Permission Group" onClose={() => setCreateOpen(false)}>
          <form onSubmit={handleCreate} className="space-y-4">
            <Field label="Group Name">
              <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="e.g. Viewer" />
            </Field>
            <Field label="Description">
              <input className="input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Optional description" />
            </Field>
            <Field label="Site Access">
              <SiteAccessControl value={form.siteAccessLevel} onChange={(v) => setForm({ ...form, siteAccessLevel: v })} />
            </Field>
            <Field label="Permissions">
              <PermissionCheckboxes selected={form.permissions} toggle={togglePerm} />
            </Field>
            <Field label="Assignable Permission Groups">
              <AssignableGroupsControl
                groups={groups}
                excludeId={null}
                selected={form.assignableGroupIds}
                toggle={toggleAssignableGroup}
              />
            </Field>
            {error && <p className="text-red-600 text-sm">{error}</p>}
            <ModalActions onCancel={() => setCreateOpen(false)} submitLabel="Create Group" />
          </form>
        </Modal>
      )}

      {/* ── Edit Modal (name / description / permissions) ─────────────────── */}
      {editTarget && (
        <Modal title={`Edit: ${editTarget.name}`} onClose={() => setEditTarget(null)}>
          <form onSubmit={handleEdit} className="space-y-4">
            <Field label="Group Name">
              <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </Field>
            <Field label="Description">
              <input className="input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </Field>
            <Field label="Site Access">
              <SiteAccessControl value={form.siteAccessLevel} onChange={(v) => setForm({ ...form, siteAccessLevel: v })} />
            </Field>
            <Field label="Permissions">
              <PermissionCheckboxes selected={form.permissions} toggle={togglePerm} />
            </Field>
            <Field label="Assignable Permission Groups">
              <AssignableGroupsControl
                groups={groups}
                excludeId={null}
                selected={form.assignableGroupIds}
                toggle={toggleAssignableGroup}
              />
            </Field>
            {error && <p className="text-red-600 text-sm">{error}</p>}
            <ModalActions onCancel={() => setEditTarget(null)} submitLabel="Save Changes" />
          </form>
        </Modal>
      )}

      {/* ── Edit Members Modal ────────────────────────────────────────────── */}
      {membersTarget && (
        <Modal title={`Members — ${membersTarget.name}`} onClose={() => setMembersTarget(null)}>
          <form onSubmit={handleMembers} className="space-y-4">
            <p className="text-sm text-gray-500">
              Select workers to assign to this group. Workers already in another group cannot be reassigned here.
            </p>
            <div className="border border-gray-200 rounded-md divide-y divide-gray-100 max-h-72 overflow-y-auto">
              {workers.length === 0 ? (
                <p className="p-4 text-sm text-gray-400">No workers available.</p>
              ) : (
                workers.map((w) => {
                  const otherGroup = workerOtherGroup(w.id, membersTarget.id);
                  const inThisGroup = memberIds.includes(w.id);
                  const locked = !!otherGroup;
                  return (
                    <label
                      key={w.id}
                      className={`flex items-center justify-between px-4 py-2.5 ${locked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <input
                          type="checkbox"
                          className="rounded flex-shrink-0"
                          checked={inThisGroup}
                          disabled={locked}
                          onChange={() => !locked && toggleMemberId(w.id)}
                        />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">{w.name}</p>
                          {w.employeeId && <p className="text-xs text-gray-400">{w.employeeId}</p>}
                        </div>
                      </div>
                      {locked && (
                        <span className="ml-3 flex-shrink-0 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                          {otherGroup.name}
                        </span>
                      )}
                    </label>
                  );
                })
              )}
            </div>
            <p className="text-xs text-gray-400">{memberIds.length} member{memberIds.length !== 1 ? 's' : ''} selected</p>
            {membersError && <p className="text-red-600 text-sm">{membersError}</p>}
            <ModalActions onCancel={() => setMembersTarget(null)} submitLabel="Save Members" />
          </form>
        </Modal>
      )}

      {/* ── View Modal ───────────────────────────────────────────────────── */}
      {viewTarget && (
        <Modal title={viewTarget.name} onClose={() => setViewTarget(null)}>
          <div className="space-y-5">
            {viewTarget.description && (
              <p className="text-sm text-gray-600">{viewTarget.description}</p>
            )}
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Site Access</h4>
              <SiteAccessBadge level={viewTarget.siteAccessLevel} />
            </div>
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                Permissions ({viewTarget.permissions?.length || 0})
              </h4>
              {(() => {
                const grps = [...new Set(ALL_PERMISSIONS.map((p) => p.group))];
                return (
                  <div className="border border-gray-200 rounded-md divide-y divide-gray-100">
                    {grps.map((grp) => (
                      <div key={grp} className="p-3 space-y-1.5">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">{grp}</p>
                        {ALL_PERMISSIONS.filter((p) => p.group === grp).map((p) => {
                          const has = viewTarget.permissions?.some((pe) => pe.permission === p.key);
                          return (
                            <div key={p.key} className={`flex items-start gap-2 p-2 rounded-md ${has ? 'bg-blue-50' : 'opacity-35'}`}>
                              <span className="mt-0.5 text-sm">{has ? '✅' : '☐'}</span>
                              <div>
                                <p className="text-sm font-medium text-gray-800">{p.label}</p>
                                <p className="text-xs text-gray-500">{p.desc}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Assignable Permission Groups</h4>
              {viewTarget.assignableGroups?.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {viewTarget.assignableGroups.map((ag) => (
                    <span key={ag.id} className="bg-blue-50 text-blue-700 text-xs px-2 py-0.5 rounded-full">{ag.name}</span>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400">Unrestricted — members can assign any permission group</p>
              )}
            </div>
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Members</h4>
              {workersInGroup(viewTarget.id).length > 0 ? (
                <div className="space-y-1">
                  {workersInGroup(viewTarget.id).map((w) => (
                    <div key={w.id} className="flex items-center justify-between text-sm py-1 border-b border-gray-100">
                      <span className="font-medium text-gray-800">{w.name}</span>
                      <span className="text-gray-400 text-xs">{w.employeeId}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400">No workers assigned to this group</p>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* ── Delete Confirm ───────────────────────────────────────────────── */}
      <ConfirmDialog
        open={!!confirm}
        title="Delete Permission Group"
        message={
          confirm?.memberCount > 0
            ? `Permanently delete group "${confirm?.name}"? ${confirm.memberCount} worker${confirm.memberCount !== 1 ? 's' : ''} will lose the permissions granted by this group.`
            : `Permanently delete group "${confirm?.name}"? Workers will lose any permissions granted by this group.`
        }
        onConfirm={async () => {
          try {
            await permissionGroupApi.remove(confirm.id);
            setConfirm(null);
            load();
          } catch (err) {
            // Leave the dialog open on failure (e.g. a race with someone
            // adding a member) so the user sees why it didn't go through,
            // instead of it silently sitting there with no feedback.
            setError(err.response?.data?.message || 'Failed to delete group');
          }
        }}
        onCancel={() => { setConfirm(null); setError(''); }}
      />

      {/* ── Set Default Confirm (step 1 of 2) ───────────────────────────────
          Setting a group as Default affects every user who has no explicit
          Permission Group assigned — this is a two-step confirmation to
          prevent an accidental click from silently changing what a large,
          possibly unknown, set of users inherit. */}
      <ConfirmDialog
        open={!!confirmDefault && confirmDefault.step === 1}
        title="Change Default Permission Group"
        danger={false}
        message={`Set "${confirmDefault?.name}" as the Default Permission Group? Any user without an explicitly assigned group will inherit this group's permissions instead of the current default.`}
        confirmLabel="Continue"
        onConfirm={() => setConfirmDefault((c) => ({ ...c, step: 2 }))}
        onCancel={() => setConfirmDefault(null)}
      />

      {/* ── Set Default Confirm (step 2 of 2, final) ────────────────────── */}
      <ConfirmDialog
        open={!!confirmDefault && confirmDefault.step === 2}
        title="Confirm Default Permission Group Change"
        danger={false}
        message={`This is final: all users with no explicitly assigned Permission Group will immediately inherit "${confirmDefault?.name}".`}
        warning="This applies as soon as you confirm — there is no separate save step after this."
        confirmLabel="Set as Default"
        onConfirm={() => handleSetDefault(confirmDefault.id)}
        onCancel={() => setConfirmDefault(null)}
      />
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

const Field = ({ label, children }) => (
  <div><label className="label">{label}</label>{children}</div>
);

const SiteAccessControl = ({ value, onChange }) => (
  <div className="border border-gray-200 rounded-md divide-y divide-gray-100">
    <label className="flex items-start gap-2 p-3 cursor-pointer">
      <input type="radio" name="siteAccessLevel" className="mt-0.5" checked={value === 'ALL'} onChange={() => onChange('ALL')} />
      <div>
        <p className="text-sm font-medium text-gray-800">All Sites</p>
        <p className="text-xs text-gray-400">Members can view and manage data for every site.</p>
      </div>
    </label>
    <label className="flex items-start gap-2 p-3 cursor-pointer">
      <input type="radio" name="siteAccessLevel" className="mt-0.5" checked={value === 'ASSIGNED_ONLY'} onChange={() => onChange('ASSIGNED_ONLY')} />
      <div>
        <p className="text-sm font-medium text-gray-800">Assigned Site Only</p>
        <p className="text-xs text-gray-400">Members are locked to their own assigned site everywhere a site selection is required — they can't view or select any other site.</p>
      </div>
    </label>
  </div>
);

const SiteAccessBadge = ({ level }) => (
  <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${
    level === 'ASSIGNED_ONLY'
      ? 'bg-teal-50 text-teal-700 border-teal-200'
      : 'bg-indigo-50 text-indigo-700 border-indigo-200'
  }`}>
    {level === 'ASSIGNED_ONLY' ? '📍 Assigned Site Only' : '🌍 All Sites'}
  </span>
);

const PermissionCheckboxes = ({ selected, toggle }) => {
  const groups = [...new Set(ALL_PERMISSIONS.map((p) => p.group))];
  return (
    <div className="border border-gray-200 rounded-md divide-y divide-gray-100 max-h-64 overflow-y-auto">
      {groups.map((grp) => (
        <div key={grp} className="p-3 space-y-2">
          <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">{grp}</p>
          {ALL_PERMISSIONS.filter((p) => p.group === grp).map((p) => (
            <label key={p.key} className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" className="mt-0.5 rounded" checked={selected.includes(p.key)} onChange={() => toggle(p.key)} />
              <div>
                <p className="text-sm font-medium text-gray-800">{p.label}</p>
                <p className="text-xs text-gray-400">{p.desc}</p>
              </div>
            </label>
          ))}
        </div>
      ))}
    </div>
  );
};

// Which OTHER permission groups can members of this group assign to a
// worker? Empty selection = unrestricted (can assign any group) — today's
// behavior, preserved by default. System groups (Full Access) are excluded
// from the picker since they can't be managed via this UI anyway, and
// assigning INTO Full Access is separately gated regardless of this list.
const AssignableGroupsControl = ({ groups, excludeId, selected, toggle }) => {
  const options = groups.filter((g) => !g.isSystem && g.id !== excludeId);
  return (
    <div>
      <p className="text-xs text-gray-400 mb-2">
        Leave empty to allow assigning any group (default). Select specific groups to restrict what members of this group can assign to a worker.
      </p>
      {options.length === 0 ? (
        <p className="text-sm text-gray-400">No other groups to choose from yet.</p>
      ) : (
        <div className="border border-gray-200 rounded-md divide-y divide-gray-100 max-h-48 overflow-y-auto">
          {options.map((g) => (
            <label key={g.id} className="flex items-center gap-2 px-3 py-2 cursor-pointer">
              <input type="checkbox" className="rounded" checked={selected.includes(g.id)} onChange={() => toggle(g.id)} />
              <span className="text-sm text-gray-800">{g.name}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

const Modal = ({ title, children, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
    <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
      </div>
      {children}
    </div>
  </div>
);

const ModalActions = ({ onCancel, submitLabel }) => (
  <div className="flex gap-3 justify-end pt-2 border-t border-gray-100">
    <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
    <button type="submit" className="btn-primary">{submitLabel}</button>
  </div>
);

function ActionMenu({ children }) {
  const [open, setOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const ref = useRef(null);
  const menuRef = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Menu height is only known once it's actually rendered, so measure right
  // after mount and flip upward if there isn't enough room below.
  useLayoutEffect(() => {
    if (!open || !ref.current || !menuRef.current) return;
    const buttonRect = ref.current.getBoundingClientRect();
    const menuHeight = menuRef.current.offsetHeight;
    const spaceBelow = window.innerHeight - buttonRect.bottom;
    setOpenUpward(spaceBelow < menuHeight + 12);
  }, [open]);

  return (
    <div className="relative flex-shrink-0" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
      >
        <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
        </svg>
      </button>
      {open && (
        <div
          ref={menuRef}
          className={`absolute right-0 z-30 w-44 bg-white rounded-xl shadow-lg border border-gray-100 py-1 ${
            openUpward ? 'bottom-full mb-1' : 'top-full mt-1'
          }`}
        >
          {Array.isArray(children)
            ? children.map((child, i) => child && <div key={i} onClick={() => setOpen(false)}>{child}</div>)
            : <div onClick={() => setOpen(false)}>{children}</div>
          }
        </div>
      )}
    </div>
  );
}

const ActionItem = ({ children, onClick, danger = false }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full text-left px-4 py-2 text-sm transition-colors ${
      danger ? 'text-red-600 hover:bg-red-50' : 'text-gray-700 hover:bg-gray-50'
    }`}
  >
    {children}
  </button>
);
